import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
// ─── Inline icons ─────────────────────────────────────────────────────────────
const CheckIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const XIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);
const ArrowRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
  </svg>
);
const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);
const HeartIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);

// ─── Spinner ──────────────────────────────────────────────────────────────────
const Spinner = ({ text = "Processing your payment…" }) => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#f0fdf9] via-white to-[#e8f8fe]">
    <div className="relative">
      <div className="w-20 h-20 rounded-full border-4 border-[#00BD7F]/20 border-t-[#00BD7F] animate-spin" />
      <div className="absolute inset-0 flex items-center justify-center">
        <HeartIcon />
      </div>
    </div>
    <p className="mt-6 text-base font-semibold text-[#004171]">{text}</p>
    <p className="mt-1 text-sm text-gray-400">Please do not close this window</p>
  </div>
);

// ─── Benefit badge ─────────────────────────────────────────────────────────────
const Benefit = ({ label }) => (
  <div className="flex items-center gap-2 bg-white/60 rounded-full px-3 py-1.5 border border-[#00BD7F]/20">
    <div className="w-4 h-4 rounded-full bg-[#00BD7F] flex items-center justify-center flex-shrink-0">
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    </div>
    <span className="text-xs text-[#004171] font-medium">{label}</span>
  </div>
);

// ─── Main ─────────────────────────────────────────────────────────────────────
function Thankyoupagedcs() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading,       setLoading]       = useState(true);
  const [paymentDone,   setPaymentDone]   = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const [txnDetails,    setTxnDetails]    = useState({ id: '', amount: '', plan: '', mode: '' });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    // Gateway returns orderId; legacy flows may use trancationid/txnid
    const orderId      = searchParams.get("orderId") || searchParams.get("trancationid") || searchParams.get("txnid");
    const urlStatus    = searchParams.get("status");
    const walletStatus = localStorage.getItem("walletstatus");
    const localTxnId   = localStorage.getItem("trancationid");

    // Hard failure from gateway
    if (urlStatus === "failure" || urlStatus === "cancel") {
      setPaymentFailed(true);
      setLoading(false);
      return;
    }

    if (orderId) {
      // Digital payment — check-status flow
      // Deduplicate: avoid re-processing on page refresh
      const processedTxns   = JSON.parse(localStorage.getItem("processedTransactions") || "[]");
      const alreadyProcessed = processedTxns.includes(orderId);

      if (alreadyProcessed) {
        setPaymentDone(true);
        setLoading(false);
      } else {
        processedTxns.push(orderId);
        localStorage.setItem("processedTransactions", JSON.stringify(processedTxns));
        checkPaymentStatus(orderId);
      }
    } else if (walletStatus === "success" && localTxnId) {
      // Wallet payment — subscription already created server-side; show success
      const subscriptionName = localStorage.getItem("subscriptionName") || "CareSathi Plan";
      const totalamount      = localStorage.getItem("totalamount") || "";
      setTxnDetails({
        id:     localTxnId,
        amount: totalamount,
        plan:   subscriptionName,
        mode:   "CureBay Wallet",
      });
      setPaymentDone(true);
      setLoading(false);
      clearPaymentStorage();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ── Update Freshworks subscription record ─────────────────────────────────
  const updateSubscriptionType = async (callbackData) => {
    try {
      const planName   = callbackData?.data?.plan_name   || callbackData?.plan_name;
      const startDate  = callbackData?.data?.start_date  || callbackData?.start_date;
      const endDate    = callbackData?.data?.end_date    || callbackData?.end_date;
      const patientCode = callbackData?.data?.patient_id || callbackData?.patient_id
                       || localStorage.getItem("patientCode");

      if (!planName || !startDate || !endDate || !patientCode) return;

      const formatDate = (d) => d ? d.split(" ")[0] : null;

      await fetch(
        `${process.env.REACT_APP_CARE_SATHI}ds/payment/freshworks/contacts/by-patient-id/${patientCode}/subscription`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subscription_type: planName,
            start_date:        formatDate(startDate),
            end_date:          formatDate(endDate),
          }),
        }
      );
    } catch (err) {
      console.error("Freshworks subscription update error:", err);
    }
  };

  // ── Record referral event (non-blocking) ──────────────────────────────────
  const recordReferralEvent = async (orderId) => {
    try {
      const retailerId = sessionStorage.getItem("retailerId") || localStorage.getItem("retailerId");
      if (retailerId) return; // Skip if retailer flow

      const referralCode = sessionStorage.getItem("validatedReferralCode")
                        || localStorage.getItem("validatedReferralCode")
                        || sessionStorage.getItem("referralCode")
                        || localStorage.getItem("referralCode");

      if (!referralCode) return;

      const patientCode    = localStorage.getItem("patientCode");
      const sourceChannel  = localStorage.getItem("sourceChannel") || "B2C";
      const subscriptionId = localStorage.getItem("subscriptionId");
      const subscriptionName = localStorage.getItem("subscriptionName") || "";

      const eventType = subscriptionName.toLowerCase().includes("half")
        ? "subscriptionPurchaseHalfYearly"
        : "subscriptionPurchaseYearly";

      await axios.post(`${process.env.REACT_APP_AFFINITY}referral/event`, {
        referralCode,
        referredPatientId: patientCode,
        sourceChannel,
        eventType,
        productType:  "Caresathi",
        referenceId:  subscriptionId,
        txnId:        orderId,
      });

      // Clear referral code after use
      ["validatedReferralCode", "referralCode"].forEach((k) => {
        localStorage.removeItem(k);
        sessionStorage.removeItem(k);
      });
    } catch (err) {
      console.error("Referral event error:", err);
    }
  };

  // ── Check payment status via API ──────────────────────────────────────────
  const checkPaymentStatus = async (orderId, attempt = 1) => {
  const MAX_ATTEMPTS = 6;
  const DELAY_MS = 3000; // 3 seconds between retries

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_PAYMENTS}paymentservices/payment/check-status`,
      { params: { orderId } }
    );

    const { orderStatus } = response.data || {};

    if (orderStatus === "SUCCESS") {
      const subscriptionName = localStorage.getItem("subscriptionName") || "CareSathi Plan";
      const totalamount      = localStorage.getItem("totalamount") || "";
      const payMode          = localStorage.getItem("payMode") || "Digital Payment";

      setTxnDetails({ id: orderId, amount: totalamount, plan: subscriptionName, mode: payMode });

      updateSubscriptionType(response.data);
      recordReferralEvent(orderId);

      setPaymentDone(true);
      setLoading(false);
      clearPaymentStorage();

    } else if (orderStatus === "FAILED") {
      // Definitive failure from gateway
      setPaymentFailed(true);
      setLoading(false);
      clearPaymentStorage();

    } else {
      // PENDING or unknown — retry
      if (attempt < MAX_ATTEMPTS) {
        setTimeout(() => checkPaymentStatus(orderId, attempt + 1), DELAY_MS);
      } else {
        // After all retries, show pending (not failed)
        setLoading(false); // shows the yellow "Payment Pending" screen
        clearPaymentStorage();
      }
    }
  } catch (err) {
    console.error("Payment status check error:", err);
    if (attempt < MAX_ATTEMPTS) {
      // Network error — retry
      setTimeout(() => checkPaymentStatus(orderId, attempt + 1), DELAY_MS);
    } else {
      
      setLoading(false);
      clearPaymentStorage();
    }
  }
};
  // ── Cleanup localStorage ──────────────────────────────────────────────────
  const clearPaymentStorage = () => {
    [
      "payMode", "totalamount", "sourceChannel", "email", "mobile",
      "redirectUrl", "subscriptionId", "subscriptionName", "paymentStatus",
      "walletstatus", "startdate", "expiry", "renew", "txnProcessed",
      "trancationid", "startDateRenewal", "duration", "amount", "actualAmount",
    ].forEach((k) => localStorage.removeItem(k));
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) return <Spinner />;

  // ── Shared page shell ──────────────────────────────────────────────────────
  const Page = ({ children }) => (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: "linear-gradient(135deg, #f0fdf9 0%, #ffffff 40%, #e8f8fe 100%)" }}
    >
      <div className="relative w-full max-w-md">
        <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#00BD7F]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-[#00B5E2]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative bg-white rounded-3xl shadow-2xl shadow-[#00BD7F]/10 border border-[#00BD7F]/10 overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );

  // ── Success ────────────────────────────────────────────────────────────────
  if (paymentDone) {
    return (
      <Page>
        <div className="h-2 bg-gradient-to-r from-[#00BD7F] to-[#00B5E2]" />
        <div className="px-8 py-10 flex flex-col items-center text-center gap-6">
          {/* Animated check */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#00BD7F] to-[#00B5E2] flex items-center justify-center shadow-lg">
              <CheckIcon />
            </div>
            <div className="absolute inset-0 rounded-full bg-[#00BD7F]/20 animate-ping" />
          </div>

          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#101010] mb-2">
              You're all set! 🎉
            </h1>
            <p className="text-base text-gray-500 leading-relaxed">
              Your <span className="font-semibold text-[#004171]">{txnDetails.plan}</span> subscription
              is now active. Your 24×7 health companion is ready.
            </p>
          </div>

          {/* Transaction details */}
          {txnDetails.id && (
            <div className="w-full bg-gradient-to-r from-[#f0fdf9] to-[#e8f8fe] rounded-2xl p-4 text-left border border-[#00BD7F]/15">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                Transaction Details
              </h3>
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Order ID</span>
                  <span className="font-semibold text-[#101010] truncate max-w-[180px]">{txnDetails.id}</span>
                </div>
                {txnDetails.amount && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Amount Paid</span>
                    <span className="font-bold text-[#00BD7F]">₹{txnDetails.amount}</span>
                  </div>
                )}
                {txnDetails.mode && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Payment Mode</span>
                    <span className="font-semibold text-[#101010]">{txnDetails.mode}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Benefits */}
          {/* <div className="w-full">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">
              Your benefits include
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {["24×7 Helpline", "Ambulance Support", "Home Nursing", "Physiotherapy", "Emergency Guidance"].map((b) => (
                <Benefit key={b} label={b} />
              ))}
            </div>
          </div> */}

          {/* CTAs */}
          <div className="w-full flex flex-col sm:flex-row gap-3 pt-2">
            <button
              onClick={() => navigate("/my-subscription")}
              className="flex-1 py-3.5 rounded-xl border-2 border-[#00BD7F] text-[#00BD7F] font-semibold text-sm hover:bg-[#f0fdf9] transition-colors"
            >
              Check Subscription
            </button>
            <button
              onClick={() => navigate("/")}
              className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-[#00BD7F] to-[#00B5E2]
                text-white font-semibold text-sm hover:opacity-90 transition-all shadow-md
                flex items-center justify-center gap-2"
            >
              Explore Services
              <ArrowRightIcon />
            </button>
          </div>

          {/* WhatsApp support */}
          {/* <a
            href={`https://wa.me/91${process.env.REACT_APP_WHATSAPP_DCS?.trim() || '7205902499'}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#25D366] font-medium hover:underline flex items-center gap-1"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Need help? Chat with us on WhatsApp
          </a> */}
        </div>
      </Page>
    );
  }

  // ── Failed ─────────────────────────────────────────────────────────────────
  if (paymentFailed) {
    return (
      <Page>
        <div className="h-2 bg-gradient-to-r from-red-400 to-red-500" />
        <div className="px-8 py-10 flex flex-col items-center text-center gap-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-red-400 to-red-500 flex items-center justify-center shadow-lg">
              <XIcon />
            </div>
            <div className="absolute inset-0 rounded-full bg-red-400/20 animate-pulse" />
          </div>

          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#101010] mb-2">
              Payment Failed
            </h1>
            <p className="text-base text-gray-500 leading-relaxed">
              We couldn't process your payment. Please try again or contact our support team.
            </p>
          </div>

          <div className="w-full bg-red-50 border border-red-100 rounded-2xl p-4 text-left">
            <p className="text-sm text-red-700">
              If money was deducted from your account, it will be refunded within 5–7 business days.
              For assistance, call us at{" "}
              <a href="tel:8335000999" className="font-bold underline">8335000999</a>.
            </p>
          </div>

          <div className="w-full flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate("/caresathi")}
              className="flex-1 py-3.5 rounded-xl border-2 border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-colors"
            >
              Back to CareSathi
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("paymentStatus");
                localStorage.removeItem("trancationid");
                localStorage.removeItem("processedTransactions");
                localStorage.removeItem("txnProcessed");
                navigate("/caresathi");
              }}
              className="flex-1 py-3.5 rounded-xl bg-gradient-to-r from-red-500 to-red-600
                text-white font-semibold text-sm hover:opacity-90 transition-all shadow-md
                flex items-center justify-center gap-2"
            >
              <RefreshIcon />
              Try Again
            </button>
          </div>
        </div>
      </Page>
    );
  }

  // ── Pending / No transaction ───────────────────────────────────────────────
  return (
    <Page>
      <div className="h-2 bg-gradient-to-r from-yellow-400 to-orange-400" />
      <div className="px-8 py-10 flex flex-col items-center text-center gap-6">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center shadow-lg">
          <ClockIcon />
        </div>

        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#101010] mb-2">
            Payment Pending
          </h1>
          <p className="text-base text-gray-500 leading-relaxed">
            Your payment is being verified. This usually takes a few minutes.
            We'll notify you once it's confirmed.
          </p>
        </div>

        <div className="w-full bg-yellow-50 border border-yellow-100 rounded-2xl p-4 text-left">
          <p className="text-sm text-yellow-800">
            If you were charged, please wait 10–15 minutes before contacting support.
            Call{" "}
            <a href="tel:8335000999" className="font-bold underline">8335000999</a> for help.
          </p>
        </div>

        <button
          onClick={() => navigate("/caresathi")}
          className="w-full py-3.5 rounded-xl bg-gradient-to-r from-[#00BD7F] to-[#00B5E2]
            text-white font-semibold text-sm hover:opacity-90 transition-all shadow-md"
        >
          Return to CareSathi
        </button>
      </div>
    </Page>
  );
}

export default Thankyoupagedcs;
